/**
 * Service to paste a previously copied timeline element at the playhead position.
 *
 * Reads the element clipboard from VideoEditor, creates a new Element model via
 * Timeline.createElement, then adds it via commandAddMediaElement (which uses
 * AddMediaElementCommand with full collision detection in DEFAULT mode).
 * Re-keys extra element data to the new ID and schedules selection.
 *
 * @module services/timeline/editing/PasteElementService
 */

import { TimelineMode } from '@src/editor/commands/TimelineMode';
import { Timeline } from '@src/editor/models/Timeline';
import { err, ok, type Result } from '@src/services/common/Result';
import { upsertElementMetaObject } from '@src/utility/extraElementData';
import { LogManager } from '@src/utility/Logger';

import type { ElementClipboardEntry } from './ElementClipboardTypes';
import type { AddMediaElementCommand } from '@src/editor/commands/AddMediaElementCommand';
import type { Element } from '@src/editor/models/Element';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';

// ---------------------------------------------------------------------------
// Structural interfaces (narrow contracts — decoupled from concrete stores)
// ---------------------------------------------------------------------------

/** Subset of VideoCreatorStore needed by PasteElementService */
export interface PasteVideoCreatorStoreLike {
  time: number;
  setActiveElements: (id: string) => void;
  currentVideo?: { extraElementData?: Record<string, any> };
}

/** Provides access to the collision-aware add command on the editor */
export interface PasteEditorLike {
  commandAddMediaElement: (
    element: Element,
    targetTrackId?: string,
    mode?: TimelineMode,
    createUndoEntry?: boolean,
  ) => AddMediaElementCommand | null;
}

/** Subset of EditorStore needed by PasteElementService */
export interface PasteEditorStoreLike {
  editor: PasteEditorLike;
}

/** Clipboard read interface (implemented by VideoEditor) */
export interface ClipboardReader {
  getElementClipboard: () => ElementClipboardEntry | null;
}

export interface PasteResultTarget {
  elementId: string;
}

export interface PasteElementServiceDeps {
  videoCreator: PasteVideoCreatorStoreLike;
  editorStore: PasteEditorStoreLike;
  videoSource: VideoSourceStore;
  clipboardHolder: ClipboardReader;
}

/**
 * Pastes the element stored in the clipboard at the current playhead position.
 *
 * - Preserves trimStart and duration from the copied element.
 * - Places the element on its original track (DEFAULT mode handles collisions).
 * - Writes extra element data under the new element's ID.
 * - Schedules selection of the pasted element after renderer state update.
 */
export class PasteElementService {
  private readonly _videoCreator: PasteVideoCreatorStoreLike;
  private readonly _editorStore: PasteEditorStoreLike;
  private readonly _videoSource: VideoSourceStore;
  private readonly _clipboard: ClipboardReader;
  private readonly _logger = LogManager.getLogger(
    'service:PasteElementService',
  );

  constructor(deps: PasteElementServiceDeps) {
    this._videoCreator = deps.videoCreator;
    this._editorStore = deps.editorStore;
    this._videoSource = deps.videoSource;
    this._clipboard = deps.clipboardHolder;
  }

  /**
   * Pastes the clipboard element at the current playhead time.
   *
   * @returns The new element's ID on success, or an error describing why paste failed.
   */
  paste(): Result<PasteResultTarget> {
    try {
      const entry = this._clipboard.getElementClipboard();
      if (!entry) {
        return err('PRECONDITION', 'Clipboard is empty — nothing to paste');
      }

      const playheadTime = this._videoCreator.time;
      const trackId = String(entry.originalTrack);
      const elementType = entry.source.type as string;

      // Build props: keep everything from the copied source but set the new time.
      // trimStart and duration are preserved from the original.
      const props: Record<string, any> = {
        ...entry.source,
        time: playheadTime,
      };

      // Construct a typed Element model (with a fresh uuid) via the factory
      const element = Timeline.createElement(elementType, props);

      // Use commandAddMediaElement which delegates to AddMediaElementCommand —
      // full collision detection, track auto-creation in DEFAULT mode, and undo.
      const command = this._editorStore.editor.commandAddMediaElement(
        element,
        trackId,
        TimelineMode.DEFAULT,
      );

      if (!command) {
        this._logger.warn('Failed to add pasted element', {
          type: elementType,
          trackId,
        });
        return err('UNKNOWN', 'Editor failed to add element');
      }

      const newId = element.id;

      // Re-key extra element data to the new element's ID
      if (entry.extraElementData) {
        upsertElementMetaObject(
          this._videoCreator.currentVideo,
          newId,
          entry.extraElementData,
        );
      }

      // Schedule selection of the pasted element after the renderer processes it
      this._videoSource.onNextStateChange(() => {
        this._videoCreator.setActiveElements(newId);
      });

      this._logger.info('Element pasted from clipboard', {
        newId,
        type: elementType,
        time: playheadTime,
      });

      return ok({ elementId: newId });
    } catch (error: unknown) {
      this._logger.error('Failed to paste element', { error });
      return err(
        'UNKNOWN',
        'Unexpected paste failure',
        error ? String(error) : undefined,
      );
    }
  }
}
